// ============================================================
//  StoreDiscounts.js — discount codes + abandoned-cart reminders
//  Customers type a code in WhatsApp (or on the store page) and it comes
//  off at checkout. Reminders nudge people who left items in their cart.
// ============================================================
import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { storeAction } from '../../api/client';
import { naira, Switch } from './storeUi';

const blank = { id: 0, code: '', type: 'percent', value: '', minTotal: '', maxUses: '', oncePerCustomer: false, endsAt: '', active: true };
const HOURS = [1, 2, 3, 4, 6, 8, 12];

export default function StoreDiscounts({ bId, store, discounts, onDiscounts, onStore }) {
  const [editing, setEditing] = useState(null);
  const [rem, setRem] = useState({ on: (store.remindAfter ?? 3) > 0, hours: store.remindAfter > 0 ? store.remindAfter : 3, code: store.remindCode || '' });
  const [busy, setBusy] = useState(false);
  const active = discounts.filter(d => d.active);

  async function toggle(d, on) {
    onDiscounts(discounts.map(x => (x.id === d.id ? { ...x, active: on } : x)));
    try { const r = await storeAction('discount_save', bId, { discount: { ...d, active: on } }); onDiscounts(r.data.discounts); }
    catch (e) { toast.error(e?.response?.data?.message || 'Could not update'); onDiscounts(discounts); }
  }
  async function saveReminders() {
    setBusy(true);
    try {
      const r = await storeAction('settings', bId, { remindAfter: rem.on ? Number(rem.hours) : 0, remindCode: rem.code });
      onStore(r.data.store); toast.success('Reminders saved');
    } catch (e) { toast.error(e?.response?.data?.message || 'Could not save'); }
    setBusy(false);
  }
  const status = d => {
    if (!d.active) return ['Off', 'muted'];
    if (d.endsAt && d.endsAt < new Date().toISOString().slice(0, 10)) return ['Expired', 'muted'];
    if (d.maxUses != null && d.used >= d.maxUses) return ['Used up', 'muted'];
    return ['Live', 'ok'];
  };

  return (
    <div className="stp-split">
      <div className="stp-col">
        <section className="stp-card" aria-labelledby="stp-codes">
          <div className="stp-card-head">
            <div><h2 id="stp-codes">Discount codes</h2><p className="stp-muted">Customers type the code in WhatsApp or on your store page, and it comes off at checkout.</p></div>
            <button type="button" className="stp-btn stp-btn-brand stp-btn-sm" onClick={() => setEditing(blank)}><i className="fa-solid fa-plus" /> New code</button>
          </div>
          {discounts.length === 0 ? (
            <div className="stp-empty-inline"><p><b>No codes yet.</b> Try <b>FIRST10</b> — 10% off, once per customer — to win first orders.</p>
              <button type="button" className="stp-btn stp-btn-dark" onClick={() => setEditing({ ...blank, code: 'FIRST10', value: '10', oncePerCustomer: true })}>Create FIRST10</button></div>
          ) : (
            <div className="stp-table-wrap">
              <table className="stp-table stp-table-sm">
                <thead><tr><th>Code</th><th>Discount</th><th>Used</th><th>On</th><th><span className="stp-sr">Edit</span></th></tr></thead>
                <tbody>
                  {discounts.map(d => {
                    const [label, tone] = status(d);
                    return (
                      <tr key={d.id}>
                        <td><b className="stp-code">{d.code}</b> <span className={`stp-pill is-${tone}`}>{label}</span>{d.oncePerCustomer && <small className="stp-muted stp-block">once per customer</small>}</td>
                        <td>{d.label}{d.endsAt && <small className="stp-muted stp-block">until {d.endsAt}</small>}</td>
                        <td>{d.used}{d.maxUses != null ? ` / ${d.maxUses}` : ''}</td>
                        <td><Switch on={d.active} onChange={on => toggle(d, on)} label={`Code ${d.code} on`} /></td>
                        <td><button type="button" className="stp-btn stp-btn-ghost stp-btn-sm" onClick={() => setEditing({ ...d, value: String(d.value), minTotal: d.minTotal ?? '', maxUses: d.maxUses ?? '', endsAt: d.endsAt || '' })}>Edit</button></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <section className="stp-card" aria-labelledby="stp-rem">
          <div className="stp-card-head"><div><h2 id="stp-rem">Cart reminders</h2>
            <p className="stp-muted">If someone adds items but doesn’t check out, the bot sends one friendly nudge with their cart. Unpaid orders get one “tap to pay” reminder too.</p></div></div>
          <div className="stp-row"><span><b>Send reminders</b><small>One message per cart — never more</small></span><Switch on={rem.on} onChange={on => setRem(r => ({ ...r, on }))} label="Send cart reminders" /></div>
          {rem.on && (
            <>
              <label className="stp-field">Send it after
                <select value={rem.hours} onChange={e => setRem(r => ({ ...r, hours: e.target.value }))}>
                  {HOURS.map(h => <option key={h} value={h}>{h} hour{h === 1 ? '' : 's'}</option>)}
                </select>
              </label>
              <label className="stp-field">Add a code to tempt them back <small>· optional</small>
                <select value={rem.code} onChange={e => setRem(r => ({ ...r, code: e.target.value }))}>
                  <option value="">No code</option>
                  {active.map(d => <option key={d.id} value={d.code}>{d.code} — {d.label}</option>)}
                </select>
              </label>
              <p className="stp-muted stp-small">WhatsApp only lets businesses message people within 24 hours of their last message, so reminders go out within that window.</p>
            </>
          )}
          <button type="button" className="stp-btn stp-btn-dark stp-self-end" onClick={saveReminders} disabled={busy}>{busy ? <span className="stp-spin" /> : null}Save reminders</button>
        </section>
      </div>

      <aside className="stp-pe-preview" aria-label="Reminder preview">
        <span className="stp-pe-k">What customers get</span>
        <div className="stp-wa">
          <div className="stp-wa-card">
            <div className="stp-wa-body">
              <span>👀 Still thinking about it? <b>Air sneakers · 42</b> is still in your cart at {store.name}.</span>
              <span className="stp-gap">1× Air sneakers · 42 — {naira(27000)}<br /><b>Total: {naira(27000)}</b></span>
              {rem.on && rem.code && <span className="stp-gap">🎁 Here’s a little something: type <b>{rem.code}</b> for {(active.find(d => d.code === rem.code) || {}).label}.</span>}
            </div>
            <div className="stp-wa-btn">Checkout</div>
            <div className="stp-wa-btn">View cart</div>
          </div>
          <small>{rem.on ? `Sent ${rem.hours} hour${Number(rem.hours) === 1 ? '' : 's'} after their last tap.` : 'Reminders are off.'}</small>
        </div>
      </aside>

      {editing && <CodeEditor bId={bId} initial={editing} onClose={() => setEditing(null)} onSaved={list => { onDiscounts(list); setEditing(null); }} />}
    </div>
  );
}

function CodeEditor({ bId, initial, onClose, onSaved }) {
  const [d, setD] = useState(initial);
  const [busy, setBusy] = useState('');
  const [confirmDel, setConfirmDel] = useState(false);
  const set = (k, v) => setD(x => ({ ...x, [k]: v }));
  const num = v => String(v).replace(/[^\d.]/g, '');
  const example = 20000;
  const off = d.type === 'percent' ? Math.round(example * (Number(d.value) || 0) / 100) : Math.min(Number(d.value) || 0, example);

  async function save() {
    setBusy('save');
    try {
      const r = await storeAction('discount_save', bId, { discount: {
        id: d.id || undefined, code: d.code, type: d.type, value: Number(d.value) || 0,
        minTotal: d.minTotal === '' ? null : Number(d.minTotal), maxUses: d.maxUses === '' ? null : Number(d.maxUses),
        oncePerCustomer: d.oncePerCustomer, endsAt: d.endsAt || null, active: d.active,
      } });
      toast.success(r.data.message); onSaved(r.data.discounts);
    } catch (e) { toast.error(e?.response?.data?.message || 'Could not save'); }
    setBusy('');
  }
  async function del() {
    if (!confirmDel) { setConfirmDel(true); return; }
    setBusy('del');
    try { const r = await storeAction('discount_delete', bId, { id: d.id }); toast.success(r.data.message); onSaved(r.data.discounts); }
    catch (e) { toast.error('Could not delete'); setBusy(''); }
  }
  return (
    <div className="stp-modal" role="dialog" aria-modal="true" aria-labelledby="stp-cd-title" onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="stp-modal-box stp-ce stp-cd">
        <div className="stp-modal-head">
          <h2 id="stp-cd-title">{d.id ? 'Edit code' : 'New discount code'}</h2>
          <button type="button" className="stp-x" aria-label="Close" onClick={onClose}><i className="fa-solid fa-xmark" /></button>
        </div>
        <div className="stp-ce-body">
          <label className="stp-field">Code customers type
            <input value={d.code} maxLength={20} onChange={e => set('code', e.target.value.toUpperCase().replace(/[^A-Z0-9_-]/g, ''))} placeholder="FIRST10" className="stp-code-in" autoFocus /></label>
          <div className="stp-field" role="group" aria-labelledby="stp-cd-type"><span id="stp-cd-type">Discount</span>
            <div className="stp-seg stp-seg-full">
              <button type="button" aria-pressed={d.type === 'percent'} className={d.type === 'percent' ? 'is-on' : ''} onClick={() => set('type', 'percent')}>% off</button>
              <button type="button" aria-pressed={d.type === 'amount'} className={d.type === 'amount' ? 'is-on' : ''} onClick={() => set('type', 'amount')}>₦ off</button>
            </div>
          </div>
          <div className="stp-grid2">
            <label className="stp-field">{d.type === 'percent' ? 'Percent off' : 'Amount off (₦)'}
              <input inputMode="decimal" value={d.value} onChange={e => set('value', num(e.target.value))} placeholder={d.type === 'percent' ? '10' : '2000'} /></label>
            <label className="stp-field">Minimum order (₦) <small>· optional</small>
              <input inputMode="decimal" value={d.minTotal} onChange={e => set('minTotal', num(e.target.value))} placeholder="None" /></label>
            <label className="stp-field">Total uses <small>· optional</small>
              <input inputMode="numeric" value={d.maxUses} onChange={e => set('maxUses', e.target.value.replace(/\D/g, ''))} placeholder="Unlimited" /></label>
            <label className="stp-field">Last day <small>· optional</small>
              <input type="date" value={d.endsAt} onChange={e => set('endsAt', e.target.value)} /></label>
          </div>
          <div className="stp-row"><span><b>Once per customer</b><small>Each WhatsApp number can use it one time</small></span><Switch on={d.oncePerCustomer} onChange={v => set('oncePerCustomer', v)} label="Once per customer" /></div>
          {Number(d.value) > 0 && <p className="stp-note">On a {naira(example)} order, <b>{d.code || 'this code'}</b> takes off <b>{naira(off)}</b>{d.minTotal && Number(d.minTotal) > example ? ` — but only on orders over ${naira(d.minTotal)}` : ''}.</p>}
        </div>
        <div className="stp-modal-foot">
          {d.id ? <button type="button" className={`stp-btn stp-btn-sm ${confirmDel ? 'stp-btn-dangerfill' : 'stp-btn-danger'}`} onBlur={() => setConfirmDel(false)} onClick={del} disabled={busy !== ''}>{confirmDel ? 'Tap again to delete' : 'Delete'}</button> : null}
          <span className="stp-flex" />
          <button type="button" className="stp-btn stp-btn-ghost" onClick={onClose}>Cancel</button>
          <button type="button" className="stp-btn stp-btn-dark" onClick={save} disabled={busy !== '' || d.code.length < 3 || !(Number(d.value) > 0)}>{busy === 'save' ? <span className="stp-spin" /> : null}Save code</button>
        </div>
      </div>
    </div>
  );
}
